import React from 'react';
import { Paper, Typography, Box, Divider, Chip, Grid } from '@mui/material';
import { Phone, Email, LinkedIn, LocationOn, Business, School, Star } from '@mui/icons-material';
import MarkdownRenderer from '../components/MarkdownRenderer';

const ProfessionalTemplate = ({ 
  resumeData, 
  customization = {},
  paperSize = 'A4'
}) => {
  const {
    primaryColor = '#2c3e50',
    secondaryColor = '#34495e',
    accentColor = '#3498db',
    fontFamily = 'Arial',
    fontSize = 11,
    spacing = 'normal'
  } = customization;

  const spacingMap = {
    compact: 0.8,
    normal: 1,
    spacious: 1.2
  };

  const currentSpacing = spacingMap[spacing] || 1;

  // Paper dimensions based on size
  const paperDimensions = {
    A4: { width: '210mm', height: '297mm' },
    Letter: { width: '8.5in', height: '11in' },
    Legal: { width: '8.5in', height: '14in' },
    A3: { width: '297mm', height: '420mm' }
  };

  const styles = {
    container: {
      width: paperDimensions[paperSize].width,
      height: paperDimensions[paperSize].height,
      fontFamily: fontFamily,
      fontSize: `${fontSize}px`,
      lineHeight: 1.4,
      margin: '0 auto',
      backgroundColor: 'white',
      display: 'flex',
      flexDirection: 'column',
      '@media print': {
        margin: 0
      }
    },
    header: {
      backgroundColor: primaryColor,
      color: 'white',
      padding: `${20 * currentSpacing}px ${24 * currentSpacing}px`,
      display: 'flex',
      alignItems: 'center',
      gap: `${20 * currentSpacing}px`
    },
    headerContent: {
      flex: 1
    },
    name: {
      fontSize: `${fontSize + 8}px`,
      fontWeight: 'bold',
      marginBottom: `${8 * currentSpacing}px`,
      letterSpacing: '1px'
    },
    title: {
      fontSize: `${fontSize + 2}px`,
      opacity: 0.9,
      marginBottom: `${12 * currentSpacing}px`,
      fontWeight: '300'
    },
    contactGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
      gap: `${12 * currentSpacing}px`,
      fontSize: `${fontSize - 1}px`
    },
    contactItem: {
      display: 'flex',
      alignItems: 'center',
      gap: '6px',
      opacity: 0.9
    },
    profilePicture: {
      width: `${80 * currentSpacing}px`,
      height: `${80 * currentSpacing}px`,
      borderRadius: '8px',
      backgroundColor: 'rgba(255,255,255,0.2)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: `${fontSize + 20}px`,
      fontWeight: 'bold',
      flexShrink: 0
    },
    content: {
      flex: 1,
      padding: `${24 * currentSpacing}px`,
      display: 'grid',
      gridTemplateColumns: '2fr 1fr',
      gap: `${30 * currentSpacing}px`
    },
    mainContent: {
      display: 'flex',
      flexDirection: 'column',
      gap: `${24 * currentSpacing}px`
    },
    sidebar: {
      display: 'flex',
      flexDirection: 'column',
      gap: `${20 * currentSpacing}px`
    },
    sectionTitle: {
      fontSize: `${fontSize + 3}px`,
      fontWeight: 'bold',
      color: primaryColor,
      marginBottom: `${12 * currentSpacing}px`,
      paddingBottom: `${6 * currentSpacing}px`,
      borderBottom: `2px solid ${accentColor}`,
      textTransform: 'uppercase',
      letterSpacing: '0.5px'
    },
    sidebarSectionTitle: {
      fontSize: `${fontSize + 2}px`,
      fontWeight: 'bold',
      color: primaryColor,
      marginBottom: `${10 * currentSpacing}px`,
      textTransform: 'uppercase',
      letterSpacing: '0.5px'
    },
    experienceItem: {
      marginBottom: `${20 * currentSpacing}px`,
      padding: `${16 * currentSpacing}px`,
      backgroundColor: '#f8f9fa',
      borderRadius: '6px',
      border: `1px solid ${accentColor}20`
    },
    experienceHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'flex-start',
      marginBottom: `${10 * currentSpacing}px`,
      flexWrap: 'wrap',
      gap: `${8 * currentSpacing}px`
    },
    jobTitle: {
      fontSize: `${fontSize + 2}px`,
      fontWeight: 'bold',
      color: primaryColor,
      marginBottom: '2px'
    },
    company: {
      fontSize: `${fontSize + 1}px`,
      color: accentColor,
      fontWeight: '600',
      display: 'flex',
      alignItems: 'center',
      gap: '4px'
    },
    duration: {
      fontSize: `${fontSize - 1}px`,
      color: '#666',
      backgroundColor: accentColor + '20',
      padding: '4px 8px',
      borderRadius: '4px',
      fontWeight: '500',
      whiteSpace: 'nowrap'
    },
    description: {
      fontSize: `${fontSize}px`,
      color: '#555',
      lineHeight: 1.5,
      marginTop: `${8 * currentSpacing}px`
    },
    summary: {
      fontSize: `${fontSize + 1}px`,
      lineHeight: 1.6,
      color: '#555',
      textAlign: 'justify',
      padding: `${16 * currentSpacing}px`,
      backgroundColor: '#f8f9fa',
      borderRadius: '6px',
      border: `1px solid ${accentColor}20`
    },
    educationItem: {
      marginBottom: `${16 * currentSpacing}px`,
      padding: `${12 * currentSpacing}px`,
      backgroundColor: '#f8f9fa',
      borderRadius: '6px',
      border: `1px solid ${accentColor}20`
    },
    degree: {
      fontSize: `${fontSize + 1}px`,
      fontWeight: 'bold',
      color: primaryColor,
      marginBottom: '4px'
    },
    institution: {
      fontSize: `${fontSize}px`,
      color: accentColor,
      fontWeight: '600',
      display: 'flex',
      alignItems: 'center',
      gap: '4px',
      marginBottom: '4px'
    },
    year: {
      fontSize: `${fontSize - 1}px`,
      color: '#666',
      backgroundColor: accentColor + '20',
      padding: '2px 6px',
      borderRadius: '4px',
      display: 'inline-block'
    },
    skillsContainer: {
      display: 'flex',
      flexWrap: 'wrap',
      gap: `${8 * currentSpacing}px`
    },
    skillChip: {
      fontSize: `${fontSize - 2}px`,
      backgroundColor: accentColor + '15',
      color: primaryColor,
      borderRadius: '4px',
      padding: '4px 8px',
      fontWeight: '500',
      border: `1px solid ${accentColor}30`
    },
    statsGrid: {
      display: 'grid',
      gridTemplateColumns: '1fr 1fr',
      gap: `${12 * currentSpacing}px`,
      marginBottom: `${16 * currentSpacing}px`
    },
    statBox: {
      textAlign: 'center',
      padding: `${12 * currentSpacing}px`,
      backgroundColor: accentColor + '10',
      borderRadius: '6px',
      border: `1px solid ${accentColor}30`
    },
    statNumber: {
      fontSize: `${fontSize + 4}px`,
      fontWeight: 'bold',
      color: primaryColor,
      display: 'block'
    },
    statLabel: {
      fontSize: `${fontSize - 2}px`,
      color: '#666',
      marginTop: '2px'
    },
    additionalInfo: {
      backgroundColor: '#f8f9fa',
      padding: `${12 * currentSpacing}px`,
      borderRadius: '6px',
      border: `1px solid ${accentColor}20`
    },
    infoItem: {
      marginBottom: `${8 * currentSpacing}px`,
      fontSize: `${fontSize - 1}px`
    },
    infoLabel: {
      fontWeight: 'bold',
      color: primaryColor,
      marginBottom: '2px'
    },
    infoValue: {
      color: '#555'
    }
  };

  const formatDuration = (startDate, endDate) => {
    const start = new Date(startDate);
    const end = endDate ? new Date(endDate) : new Date();
    const months = (end.getFullYear() - start.getFullYear()) * 12 + (end.getMonth() - start.getMonth());
    const years = Math.floor(months / 12);
    const remainingMonths = months % 12;
    
    if (years === 0) return `${remainingMonths} mo`;
    if (remainingMonths === 0) return `${years} yr`;
    return `${years} yr ${remainingMonths} mo`;
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
  };

  const getTotalExperience = () => {
    if (!resumeData.experience || resumeData.experience.length === 0) return 0;
    
    let totalMonths = 0;
    resumeData.experience.forEach(exp => {
      const start = new Date(exp.startDate);
      const end = exp.endDate ? new Date(exp.endDate) : new Date();
      totalMonths += (end.getFullYear() - start.getFullYear()) * 12 + (end.getMonth() - start.getMonth());
    });
    
    return Math.floor(totalMonths / 12);
  };

  const getInitials = () => {
    const firstName = resumeData.personalInfo?.firstName || '';
    const lastName = resumeData.personalInfo?.lastName || '';
    return (firstName.charAt(0) + lastName.charAt(0)).toUpperCase();
  };

  return (
    <Paper sx={styles.container} elevation={0}>
      {/* Header Section */}
      <Box sx={styles.header}>
        <Box sx={styles.headerContent}>
          <Typography sx={styles.name}>
            {resumeData.personalInfo?.firstName} {resumeData.personalInfo?.lastName}
          </Typography>
          
          {resumeData.personalInfo?.title && (
            <Typography sx={styles.title}>
              {resumeData.personalInfo.title}
            </Typography>
          )}

          <Box sx={styles.contactGrid}>
            {resumeData.personalInfo?.email && (
              <Box sx={styles.contactItem}>
                <Email sx={{ fontSize: `${fontSize + 2}px` }} />
                <span>{resumeData.personalInfo.email}</span>
              </Box>
            )}
            {resumeData.personalInfo?.phone && (
              <Box sx={styles.contactItem}>
                <Phone sx={{ fontSize: `${fontSize + 2}px` }} />
                <span>{resumeData.personalInfo.phone}</span>
              </Box>
            )}
            {resumeData.personalInfo?.linkedinUrl && (
              <Box sx={styles.contactItem}>
                <LinkedIn sx={{ fontSize: `${fontSize + 2}px` }} />
                <span>LinkedIn Profile</span>
              </Box>
            )}
            {resumeData.personalInfo?.location && (
              <Box sx={styles.contactItem}>
                <LocationOn sx={{ fontSize: `${fontSize + 2}px` }} />
                <span>{resumeData.personalInfo.location}</span>
              </Box>
            )}
          </Box>
        </Box>

        <Box sx={styles.profilePicture}>
          {getInitials()}
        </Box>
      </Box>

      {/* Content Section */}
      <Box sx={styles.content}>
        {/* Main Content */}
        <Box sx={styles.mainContent}>
          {/* Professional Summary */}
          {resumeData.personalInfo?.summary && (
            <Box>
              <Typography sx={styles.sectionTitle}>Professional Summary</Typography>
              <MarkdownRenderer
                content={resumeData.personalInfo.summary}
                typography={styles.summary}
              />
            </Box>
          )}

          {/* Experience Section */}
          {resumeData.experience && resumeData.experience.length > 0 && (
            <Box>
              <Typography sx={styles.sectionTitle}>Professional Experience</Typography>
              {resumeData.experience.map((exp, index) => (
                <Box key={index} sx={styles.experienceItem}>
                  <Box sx={styles.experienceHeader}>
                    <Box>
                      <Typography sx={styles.jobTitle}>{exp.title}</Typography>
                      <Typography sx={styles.company}>
                        <Business sx={{ fontSize: `${fontSize}px` }} />
                        {exp.company}
                      </Typography>
                    </Box>
                    <Typography sx={styles.duration}>
                      {formatDate(exp.startDate)} - {exp.endDate ? formatDate(exp.endDate) : 'Present'}
                      <br />({formatDuration(exp.startDate, exp.endDate)})
                    </Typography>
                  </Box>
                  
                  {exp.description && (
                    <MarkdownRenderer
                      content={exp.description}
                      typography={styles.description}
                    />
                  )}

                  {exp.skills && exp.skills.length > 0 && (
                    <Box sx={{ marginTop: `${12 * currentSpacing}px` }}>
                      <Typography sx={{ 
                        fontSize: `${fontSize - 1}px`, 
                        fontWeight: 'bold', 
                        color: primaryColor, 
                        marginBottom: `${6 * currentSpacing}px` 
                      }}>
                        Key Technologies:
                      </Typography>
                      <Box sx={styles.skillsContainer}>
                        {exp.skills.map((skill, skillIndex) => (
                          <span key={skillIndex} sx={styles.skillChip}>
                            {typeof skill === 'object' && skill !== null ? (typeof skill.name === 'string' ? skill.name : (skill.name ? String(skill.name) : (skill.id ? String(skill.id) : '[Skill]'))) : String(skill)}
                          </span>
                        ))}
                      </Box>
                    </Box>
                  )}
                </Box>
              ))}
            </Box>
          )}

          {/* Education Section */}
          {resumeData.education && resumeData.education.length > 0 && (
            <Box>
              <Typography sx={styles.sectionTitle}>Education</Typography>
              {resumeData.education.map((edu, index) => (
                <Box key={index} sx={styles.educationItem}>
                  <Typography sx={styles.degree}>{edu.degree}</Typography>
                  <Typography sx={styles.institution}>
                    <School sx={{ fontSize: `${fontSize}px` }} />
                    {edu.institution}
                  </Typography>
                  <Box sx={{ marginTop: '4px' }}>
                    {edu.graduationYear && (
                      <Typography sx={styles.year}>
                        {edu.graduationYear}
                      </Typography>
                    )}
                    {edu.grade && (
                      <Typography sx={{ 
                        ...styles.year, 
                        marginLeft: `${8 * currentSpacing}px`,
                        backgroundColor: accentColor + '30'
                      }}>
                        Grade: {edu.grade}
                      </Typography>
                    )}
                  </Box>
                  {edu.description && (
                    <Box sx={{ mt: 1 }}>
                      <MarkdownRenderer
                        content={edu.description}
                        typography={styles.description}
                      />
                    </Box>
                  )}
                </Box>
              ))}
            </Box>
          )}
        </Box>

        {/* Sidebar */}
        <Box sx={styles.sidebar}>
          {/* Career Stats */}
          <Box>
            <Typography sx={styles.sidebarSectionTitle}>Career Overview</Typography>
            <Box sx={styles.statsGrid}>
              <Box sx={styles.statBox}>
                <Typography sx={styles.statNumber}>{getTotalExperience()}+</Typography>
                <Typography sx={styles.statLabel}>Years Exp.</Typography>
              </Box>
              <Box sx={styles.statBox}>
                <Typography sx={styles.statNumber}>{resumeData.experience?.length || 0}</Typography>
                <Typography sx={styles.statLabel}>Companies</Typography>
              </Box>
              <Box sx={styles.statBox}>
                <Typography sx={styles.statNumber}>{resumeData.skills?.length || 0}</Typography>
                <Typography sx={styles.statLabel}>Skills</Typography>
              </Box>
              <Box sx={styles.statBox}>
                <Typography sx={styles.statNumber}>{resumeData.education?.length || 0}</Typography>
                <Typography sx={styles.statLabel}>Degrees</Typography>
              </Box>
            </Box>
          </Box>

          {/* Skills Section */}
          {resumeData.skills && resumeData.skills.length > 0 && (
            <Box>
              <Typography sx={styles.sidebarSectionTitle}>Core Skills</Typography>
              <Box sx={styles.skillsContainer}>
                {resumeData.skills.map((skill, index) => (
                  <span key={index} style={styles.skillChip}>
                    {typeof skill === 'object' && skill !== null ? (typeof skill.name === 'string' ? skill.name : (skill.name ? String(skill.name) : (skill.id ? String(skill.id) : '[Skill]'))) : String(skill)}
                  </span>
                ))}
              </Box>
            </Box>
          )}

          {/* Additional Information */}
          {(resumeData.personalInfo?.currentSalary || resumeData.personalInfo?.noticePeriod) && (
            <Box sx={styles.additionalInfo}>
              <Typography sx={styles.sidebarSectionTitle}>Additional Info</Typography>
              {resumeData.personalInfo.currentSalary && (
                <Box sx={styles.infoItem}>
                  <Typography sx={styles.infoLabel}>Current Salary</Typography>
                  <Typography sx={styles.infoValue}>
                    {resumeData.personalInfo.currentSalary}
                  </Typography>
                </Box>
              )}
              {resumeData.personalInfo.noticePeriod && (
                <Box sx={styles.infoItem}>
                  <Typography sx={styles.infoLabel}>Notice Period</Typography>
                  <Typography sx={styles.infoValue}>
                    {resumeData.personalInfo.noticePeriod}
                  </Typography>
                </Box>
              )}
            </Box>
          )}
        </Box>
      </Box>
    </Paper>
  );
};

export default ProfessionalTemplate;
